/**
 * Audit Log — Hash-chained, tamper-evident audit trail.
 *
 * This is the "immutable proof" page. Every action taken in ArgusAI is
 * cryptographically hash-chained here: each record embeds the previous
 * record's hash, so a single altered row invalidates the entire chain.
 *
 * Retention:
 *  - ITAR:  5 years
 *  - CMMC:  3 years
 *  - HIPAA: 6 years
 *  - GDPR:  configurable (we default to 6)
 *
 * Features:
 *  - Hash Chain Status banner (verify button, integrity state, last sequence)
 *  - Stats bar (24h totals, successful, failed, critical)
 *  - Filter row (search, category, outcome, date range, user, framework)
 *  - Sortable table (via useTableSort + SortableHeader)
 *  - Detail Sheet (full record, hash-chain proof, related events, metadata JSON)
 *  - Hash Chain Verification dialog (live progress, broken-link report)
 *  - Forensics Mode toggle (session correlation, tool-call visualization)
 *  - Real-time Mode toggle (auto-append new events every 5s)
 *  - Exports: CSV / JSON / OSCAL, filtered or date-ranged
 *  - Classification banners (CUI / PHI / ITAR) + framework badges
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTrustAudit } from '@/lib/use-trust-module';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import {
  ScrollText, Hash, ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
  Search, Download, RefreshCw, Eye, Activity, Clock, User, FileText,
  MoreHorizontal, Link2, ArrowUpRight, Lock, Play, Pause, Calendar,
  Radio, ChevronRight, Copy, Layers, Terminal, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { apiClient } from '@/lib/api-client';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { triggerDownload, generateCsv } from '@/lib/trust-downloads';

interface AuditSearch {
  q?: string;
}

export const Route = createFileRoute('/trust/audit')({
  component: AuditLogPage,
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
});

// ── Types ───────────────────────────────────────────────────────────────────

type AuditCategory =
  | 'authentication'
  | 'ai_query'
  | 'tool_call'
  | 'data_modification'
  | 'governance'
  | 'approval'
  | 'compliance'
  | 'admin_action'
  | 'breach_response'
  | 'data_subject_request';

type AuditOutcome = 'success' | 'failure' | 'blocked';

type AuditClassification = 'Unclassified' | 'CUI' | 'PHI' | 'ITAR';

type AuditFramework = 'ITAR' | 'CMMC L2' | 'HIPAA' | 'GDPR' | 'SOC 2' | 'ISO 27001';

interface AuditEntry {
  id: string;
  sequenceNumber: number;
  timestamp: string;
  category: AuditCategory;
  action: string;
  resource: string;
  outcome: AuditOutcome;
  userId: string;
  userName: string;
  userEmail: string;
  ipAddress: string;
  userAgent: string;
  hash: string;              // short mock (16 hex)
  fullHash: string;          // 64-char hex
  previousHash: string;      // 64-char hex
  classification: AuditClassification;
  frameworks: AuditFramework[];
  sessionId: string;
  conversationId?: string;
  detailedMetadata?: Record<string, unknown>;
  critical?: boolean;
}

// ── Mock Data Generator ─────────────────────────────────────────────────────

// Hardcoded USERS array removed — the User filter now sources from
// real /users API (see useTrustEntities() in AuditLogPage). The legacy
// `buildMockAuditLog()` helper that referenced this array is dead code
// not invoked anywhere in the rendered tree.

const USER_AGENTS = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
  'ArgusAI-CLI/1.4.2 (linux; x64)',
  'IQ-Service/2.1.0 (node-20)',
];

function randomHex(length: number): string {
  const chars = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < length; i++) out += chars[Math.floor(Math.random() * 16)];
  return out;
}

function shortHash(full: string): string {
  return `${full.slice(0, 8)}…${full.slice(-4)}`;
}

interface ActionTemplate {
  action: string;
  resource: string;
  outcome: AuditOutcome;
  classification: AuditClassification;
  frameworks: AuditFramework[];
  critical?: boolean;
  metadata?: Record<string, unknown>;
}

const TEMPLATES: Record<AuditCategory, ActionTemplate[]> = {
  authentication: [
    { action: 'login_success',    resource: 'session:sess-9f2c8a1',  outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'], metadata: { method: 'sso_okta', mfa: true } },
    { action: 'login_failure',    resource: 'email:unknown@ext.com', outcome: 'failure', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { reason: 'invalid_credentials', attempt: 3 } },
    { action: 'logout',           resource: 'session:sess-9f2c8a1',  outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'] },
    { action: 'mfa_challenge',    resource: 'user:u-001',            outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { factor: 'totp' } },
    { action: 'password_reset',   resource: 'user:u-003',            outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'] },
    { action: 'sso_initiated',    resource: 'idp:okta-prod',         outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { idp: 'Okta' } },
    { action: 'login_failure',    resource: 'user:u-002',            outcome: 'blocked', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], critical: true, metadata: { reason: 'impossible_travel', geo_a: 'US-TX', geo_b: 'RU-MOW' } },
  ],
  ai_query: [
    { action: 'query_submitted',   resource: 'conv:c-18234',         outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'], metadata: { modelId: 'qwen-2.5-72b-awq', tokens_in: 128 } },
    { action: 'response_streamed', resource: 'conv:c-18234',         outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { tokens_out: 842, latency_ms: 3420, first_token_ms: 1245 } },
    { action: 'citation_generated',resource: 'doc:sop-0412-rev5',    outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2'], metadata: { page: 14, section: '4.3.2' } },
    { action: 'follow_up_suggested',resource: 'conv:c-18234',        outcome: 'success', classification: 'Unclassified', frameworks: [] },
    { action: 'query_submitted',   resource: 'conv:c-18291',         outcome: 'blocked', classification: 'ITAR', frameworks: ['ITAR', 'CMMC L2'], critical: true, metadata: { reason: 'us_person_verification_failed' } },
  ],
  tool_call: [
    { action: 'tool_invoked',       resource: 'iq.asset.search',       outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { args: { facility: 'plant-7', type: 'pump' } } },
    { action: 'tool_invoked',       resource: 'iq.cmms.work_order_get', outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { woId: 'WO-2034881' } },
    { action: 'tool_result',        resource: 'iq.telemetry.query',    outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { rows: 288, latency_ms: 142 } },
    { action: 'tool_error',         resource: 'ext.sap.po_lookup',     outcome: 'failure', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { error: 'upstream_timeout', timeout_ms: 90000 } },
    { action: 'tool_blocked_by_policy', resource: 'iq.cmms.work_order_create', outcome: 'blocked', classification: 'CUI', frameworks: ['CMMC L2', 'ITAR'], critical: true, metadata: { policy: 'write_requires_approval', approver_needed: 'supervisor' } },
    { action: 'tool_invoked',       resource: 'iq.document.search',    outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2'], metadata: { query: 'LOTO procedure pump P-401' } },
    { action: 'tool_invoked',       resource: 'iq.analytics.mtbf',     outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { assetId: 'pump-p-401', period: '90d' } },
  ],
  data_modification: [
    { action: 'document_uploaded',  resource: 'doc:sop-0412-rev6',     outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2', 'ITAR'], metadata: { bytes: 2_489_112, type: 'application/pdf' } },
    { action: 'document_deleted',   resource: 'doc:draft-8812',        outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'GDPR'] },
    { action: 'conversation_archived', resource: 'conv:c-18101',       outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'] },
    { action: 'memory_created',     resource: 'agent:maintenance-bot', outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { scope: 'user', ttl_days: 90 } },
    { action: 'document_deleted',   resource: 'doc:phi-intake-419',    outcome: 'success', classification: 'PHI', frameworks: ['HIPAA'], metadata: { retention_expired: true } },
  ],
  governance: [
    { action: 'guardrail_triggered', resource: 'guardrail:topic_drift',  outcome: 'blocked', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'], metadata: { profile: 'standard', latency_ms: 212 } },
    { action: 'pii_detected_redacted', resource: 'conv:c-18205',         outcome: 'success', classification: 'Unclassified', frameworks: ['GDPR', 'HIPAA'], metadata: { pii_types: ['ssn', 'email'], count: 2 } },
    { action: 'prompt_injection_blocked', resource: 'conv:c-18244',      outcome: 'blocked', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'], critical: true, metadata: { pattern: 'system_override', confidence: 0.94 } },
    { action: 'classification_violation', resource: 'conv:c-18260',      outcome: 'blocked', classification: 'ITAR', frameworks: ['ITAR', 'CMMC L2'], critical: true, metadata: { requested: 'ITAR', cleared: 'CUI' } },
    { action: 'guardrail_triggered', resource: 'guardrail:pii_output',   outcome: 'blocked', classification: 'PHI', frameworks: ['HIPAA'], metadata: { field: 'patient_dob' } },
  ],
  approval: [
    { action: 'approval_requested', resource: 'wo:WO-2034901-draft',    outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2'], metadata: { tier: 'supervisor', expires_in_hours: 24 } },
    { action: 'approval_granted',   resource: 'wo:WO-2034901-draft',    outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2', 'SOC 2'], metadata: { approver: 'u-006', elapsed_min: 14 } },
    { action: 'approval_denied',    resource: 'doc:export-req-118',     outcome: 'failure', classification: 'ITAR', frameworks: ['ITAR'], critical: true, metadata: { reason: 'us_person_not_verified' } },
    { action: 'approval_expired',   resource: 'wo:WO-2034888-draft',    outcome: 'failure', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { expired_after_hours: 24 } },
  ],
  compliance: [
    { action: 'evidence_uploaded',    resource: 'ev:access-review-q2',  outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { reviewer: 'u-005', size_bytes: 184_204 } },
    { action: 'control_test_passed',  resource: 'ctrl:AC-2',            outcome: 'success', classification: 'Unclassified', frameworks: ['CMMC L2', 'ITAR'], metadata: { framework: 'CMMC L2', testedBy: 'u-007' } },
    { action: 'control_test_failed',  resource: 'ctrl:SI-4',            outcome: 'failure', classification: 'Unclassified', frameworks: ['CMMC L2'], critical: true, metadata: { finding: 'Alerting rule inactive for 48h' } },
    { action: 'framework_status_changed', resource: 'framework:hipaa',  outcome: 'success', classification: 'PHI', frameworks: ['HIPAA'], metadata: { from: 'implementing', to: 'live' } },
  ],
  admin_action: [
    { action: 'user_created',       resource: 'user:u-010',            outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { role: 'member', invited_by: 'u-001' } },
    { action: 'role_assigned',      resource: 'user:u-008',            outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'CMMC L2'], metadata: { role: 'power_user', prev: 'member' } },
    { action: 'agent_deployed',     resource: 'agent:shift-handover',  outcome: 'success', classification: 'CUI', frameworks: ['CMMC L2', 'SOC 2'], metadata: { version: '1.4.0', shadowFor: '7d' } },
    { action: 'mcp_server_added',   resource: 'mcp:ext.sap-s4',        outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'], metadata: { tier: 2, sandbox: 'docker', network: 'iqbridge' } },
    { action: 'settings_changed',   resource: 'tenant:acme-prod',      outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2'], metadata: { field: 'guardrail_profile', from: 'basic', to: 'standard' } },
    { action: 'role_assigned',      resource: 'user:u-003',            outcome: 'blocked', classification: 'ITAR', frameworks: ['ITAR'], critical: true, metadata: { reason: 'clearance_insufficient', requested: 'itar_admin' } },
  ],
  breach_response: [
    { action: 'breach_detected',    resource: 'breach:b-2026-011',     outcome: 'success', classification: 'PHI', frameworks: ['HIPAA', 'GDPR'], critical: true, metadata: { severity: 'high', records_affected: 482 } },
    { action: 'breach_contained',   resource: 'breach:b-2026-011',     outcome: 'success', classification: 'PHI', frameworks: ['HIPAA', 'GDPR'], metadata: { containment_time_min: 38 } },
    { action: 'notification_sent',  resource: 'breach:b-2026-011',     outcome: 'success', classification: 'PHI', frameworks: ['HIPAA', 'GDPR'], metadata: { channel: 'email', recipients: 482, clock: 'gdpr_72h' } },
    { action: 'post_mortem_created',resource: 'breach:b-2026-008',     outcome: 'success', classification: 'Unclassified', frameworks: ['SOC 2', 'ISO 27001'] },
  ],
  data_subject_request: [
    { action: 'dsar_received',      resource: 'dsar:ds-2026-044',      outcome: 'success', classification: 'Unclassified', frameworks: ['GDPR'], metadata: { type: 'access', deadline_days: 30 } },
    { action: 'dsar_completed',     resource: 'dsar:ds-2026-039',      outcome: 'success', classification: 'Unclassified', frameworks: ['GDPR'], metadata: { completed_in_days: 11 } },
    { action: 'data_exported',      resource: 'dsar:ds-2026-044',      outcome: 'success', classification: 'Unclassified', frameworks: ['GDPR'], metadata: { format: 'json', rows: 1_482 } },
    { action: 'data_deleted',       resource: 'dsar:ds-2026-041',      outcome: 'success', classification: 'Unclassified', frameworks: ['GDPR'], metadata: { right: 'erasure', rows: 844 } },
  ],
};

// `buildMockAuditLog` removed — audit entries now come from
// `useTrustAudit()` (real /trust-module/audit endpoint).

// ── Style maps ──────────────────────────────────────────────────────────────

const CATEGORY_STYLES: Record<AuditCategory, { label: string; color: string; icon: typeof ScrollText }> = {
  authentication:       { label: 'Auth',           color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',             icon: Lock },
  ai_query:             { label: 'AI Query',       color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',     icon: Activity },
  tool_call:            { label: 'Tool Call',      color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',             icon: Terminal },
  data_modification:    { label: 'Data Mod',       color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',     icon: FileText },
  governance:           { label: 'Governance',     color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',         icon: ShieldCheck },
  approval:             { label: 'Approval',       color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  compliance:           { label: 'Compliance',     color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',             icon: Layers },
  admin_action:         { label: 'Admin',          color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',     icon: User },
  breach_response:      { label: 'Breach',         color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',                 icon: AlertTriangle },
  data_subject_request: { label: 'DSAR',           color: 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',             icon: User },
};

const OUTCOME_STYLES: Record<AuditOutcome, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  success: { label: 'Success', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  failure: { label: 'Failure', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',                 icon: XCircle },
  blocked: { label: 'Blocked', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',     icon: AlertTriangle },
};

const CLASSIFICATION_STYLES: Record<AuditClassification, { color: string; banner: string }> = {
  Unclassified: { color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',               banner: '' },
  CUI:          { color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',           banner: 'bg-amber-500/[0.03] border-l-2 border-l-amber-500/60' },
  PHI:          { color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',   banner: 'bg-emerald-500/[0.03] border-l-2 border-l-emerald-500/60' },
  ITAR:         { color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',                   banner: 'bg-red-500/[0.03] border-l-2 border-l-red-500/60' },
};

const FRAMEWORK_COLORS: Record<AuditFramework, string> = {
  'ITAR':       'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  'CMMC L2':    'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  'HIPAA':      'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  'GDPR':       'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  'SOC 2':      'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'ISO 27001':  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied to clipboard`);
  }).catch(() => toast.error('Copy failed'));
}

// ── Page ────────────────────────────────────────────────────────────────────

function AuditLogPage() {
  const { q: initialQuery } = Route.useSearch();
  // Real-data: audit_logs adapted to AuditEntry VM.
  // 2026-05-12 browser audit P1 — destructure isError/error so the page
  // can surface a real failure instead of pretending the audit log is
  // empty when the backend returned HTTP 500.
  const {
    data: realAudit,
    isError: auditError,
    error: auditErrorObj,
  } = useTrustAudit();
  const entities = useTrustEntities();
  const queryClient = useQueryClient();
  // Real chain status (drives the banner honestly instead of a hardcoded
  // "not yet enabled" claim that the working verify endpoint contradicts).
  const chainStatusQuery = useQuery<{
    enabled: boolean;
    totalRecords: number;
    lastSequenceNumber: number;
    lastVerifiedAt: string | null;
  }>({
    queryKey: ['trust', 'audit-chain', 'status'],
    queryFn: () => apiClient.get('/governance/audit/chain/status'),
  });
  const chainEnabled = chainStatusQuery.data?.enabled ?? false;
  // Map server categories (auth/system/etc.) to mock UI enum.
  const SERVER_CAT_MAP: Record<string, AuditCategory> = {
    auth: 'authentication',
    authentication: 'authentication',
    ai_query: 'ai_query',
    tool_call: 'tool_call',
    data: 'data_modification',
    data_modification: 'data_modification',
    governance: 'governance',
    approval: 'approval',
    compliance: 'compliance',
    admin: 'admin_action',
    admin_action: 'admin_action',
    breach_response: 'breach_response',
    data_subject_request: 'data_subject_request',
  };
  const SERVER_OUT_MAP: Record<string, AuditOutcome> = {
    success: 'success',
    failure: 'failure',
    blocked: 'blocked',
    denied: 'blocked',
    warning: 'failure',
  };
  // Depend on stable underlying user array reference + audit entries.
  // Including `entities` (fresh object every render) caused an infinite
  // re-render loop (React Error #185).
  const usersRef = entities.users;
  // Platform audit 2026-06-12: this was a useState + useEffect(setEntries) that
  // MIRRORED derived data — the classic React #185 anti-pattern. When react-query
  // handed back a new `entries`/`users` reference on a refetch (which a dialog-open
  // re-render or the verify call could trigger), the effect re-ran → setEntries →
  // re-render → new reference → infinite loop → "Maximum update depth" CRASH that
  // replaced the whole Audit page with an error boundary (intermittent, timing-
  // dependent). Derive with useMemo instead: it recomputes but never setStates, so
  // it CANNOT loop. (An earlier partial fix only stabilized the dep; this removes
  // the setState entirely.)
  const entries = useMemo<AuditEntry[]>(() => {
    if (!realAudit?.entries) return [];
    const userById = new Map(usersRef.map(u => [u.id, u]));
    return realAudit.entries.map((r): AuditEntry => {
      const realUser = r.userId ? userById.get(r.userId) : undefined;
      const userName = realUser?.name
        ?? r.userEmail
        ?? (r.userId ? `user:${shortId(r.userId)}` : '—');
      return {
        id: r.id,
        sequenceNumber: r.sequenceNumber ?? 0,
        timestamp: r.createdAt,
        category: SERVER_CAT_MAP[r.category ?? ''] ?? 'admin_action',
        action: r.action,
        resource: `${r.resourceType}${r.resourceId ? `:${r.resourceId.slice(0,8)}` : ''}`,
        outcome: SERVER_OUT_MAP[r.outcome ?? ''] ?? 'success',
        userId: r.userId ?? '',
        userName,
        userEmail: realUser?.email ?? r.userEmail ?? '',
        ipAddress: r.ipAddress ?? '',
        userAgent: '',
        hash: (r.recordHash ?? '').slice(0, 16),
        fullHash: r.recordHash ?? '',
        previousHash: r.previousHash ?? '',
        classification: 'Unclassified' as AuditClassification,
        frameworks: [],
        sessionId: '',
        detailedMetadata: r.metadata ?? undefined,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realAudit?.entries, usersRef, SERVER_CAT_MAP, SERVER_OUT_MAP]);
  const [search, setSearch] = useState(initialQuery ?? '');
  const [categoryFilter, setCategoryFilter] = useState<'all' | AuditCategory>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | AuditOutcome>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<'all' | AuditFramework>('all');
  const [dateFilter, setDateFilter] = useState<'1h' | '24h' | '7d' | '30d' | 'all'>('24h');

  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [forensicsMode, setForensicsMode] = useState(false);
  const [realtimeMode, setRealtimeMode] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [customRange, setCustomRange] = useState<{ start: string; end: string } | null>(null);

  // Real-time: synthetic stream removed — page now renders the
  // tail of the real audit_logs cursor, refreshed via the query
  // client when realtimeMode is on.
  const seqRef = useRef<number>(entries[0]?.sequenceNumber ?? 0);
  void seqRef;
  useEffect(() => {
    if (!realtimeMode) return;
    // Real 5s auto-refresh: invalidate the audit query so react-query
    // refetches the latest rows (advances last-sequence / row count).
    const id = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['trust-module', 'audit'] });
    }, 5000);
    return () => clearInterval(id);
  }, [realtimeMode, queryClient]);

  // Date window
  const dateFiltered = useMemo(() => {
    // Custom range takes precedence over preset
    if (customRange) {
      const startMs = new Date(customRange.start).getTime();
      const endMs = new Date(customRange.end).getTime() + 86_400_000 - 1; // inclusive end-of-day
      return entries.filter(e => {
        const t = new Date(e.timestamp).getTime();
        return t >= startMs && t <= endMs;
      });
    }
    if (dateFilter === 'all') return entries;
    const now = Date.now();
    const window: Record<Exclude<typeof dateFilter, 'all'>, number> = {
      '1h':  1 * 3600_000,
      '24h': 24 * 3600_000,
      '7d':  7 * 86_400_000,
      '30d': 30 * 86_400_000,
    };
    const cutoff = now - window[dateFilter];
    return entries.filter(e => new Date(e.timestamp).getTime() >= cutoff);
  }, [entries, dateFilter, customRange]);

  // Stats (last 24h slice)
  const stats24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600_000;
    const in24h = entries.filter(e => new Date(e.timestamp).getTime() >= cutoff);
    return {
      total:      in24h.length,
      successful: in24h.filter(e => e.outcome === 'success').length,
      failed:     in24h.filter(e => e.outcome === 'failure').length,
      critical:   in24h.filter(e => !!e.critical).length,
    };
  }, [entries]);

  // Filter pipeline
  const filtered = useMemo(() => {
    return dateFiltered.filter(e => {
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (outcomeFilter !== 'all' && e.outcome !== outcomeFilter) return false;
      if (userFilter !== 'all' && e.userId !== userFilter) return false;
      if (frameworkFilter !== 'all' && !e.frameworks.includes(frameworkFilter)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return e.action.toLowerCase().includes(q)
        || e.resource.toLowerCase().includes(q)
        || e.userName.toLowerCase().includes(q)
        || e.userEmail.toLowerCase().includes(q)
        || e.ipAddress.includes(q)
        || e.hash.includes(q);
    });
  }, [dateFiltered, search, categoryFilter, outcomeFilter, userFilter, frameworkFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'sequenceNumber', 'desc');

  // Related events (same session or conversation)
  const relatedFor = (e: AuditEntry) =>
    entries.filter(x =>
      x.id !== e.id
      && (x.sessionId === e.sessionId || (e.conversationId && x.conversationId === e.conversationId))
    ).slice(0, 8);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-primary" /> Audit Log
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hash-chained, tamper-evident audit trail. Retention: ITAR 5y · CMMC 3y · HIPAA 6y.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-card">
            <Radio className={cn('h-3.5 w-3.5', realtimeMode ? 'text-emerald-500 animate-pulse' : 'text-muted-foreground')} />
            <span className="text-xs font-medium">Real-time</span>
            <Switch
              checked={realtimeMode}
              onCheckedChange={v => {
                setRealtimeMode(v);
                toast.info(v ? 'Real-time mode enabled — auto-refresh every 5s' : 'Real-time mode disabled');
              }}
            />
          </div>
          <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-card">
            <Eye className={cn('h-3.5 w-3.5', forensicsMode ? 'text-violet-500' : 'text-muted-foreground')} />
            <span className="text-xs font-medium">Forensics</span>
            <Switch
              checked={forensicsMode}
              onCheckedChange={v => {
                setForensicsMode(v);
                toast.info(v ? 'Forensics mode — additional correlation fields visible' : 'Forensics mode off');
              }}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1.5" title="Export audit log">
                <Download className="h-3.5 w-3.5" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Export Filtered ({filtered.length})</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => {
                const csv = generateCsv(
                  ['sequence', 'timestamp', 'category', 'action', 'resource', 'user', 'outcome', 'classification', 'hash'],
                  filtered.map(e => [e.sequenceNumber, e.timestamp, e.category, e.action, e.resource, e.userName, e.outcome, e.classification, e.fullHash]),
                );
                triggerDownload({ filename: `audit-log-${new Date().toISOString().slice(0,10)}.csv`, content: csv, mimeType: 'text/csv' });
                toast.success(`Exported ${filtered.length} rows as CSV`);
              }}>
                <FileText className="h-3.5 w-3.5 mr-2" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                triggerDownload({ filename: `audit-log-${new Date().toISOString().slice(0,10)}.json`, content: JSON.stringify(filtered, null, 2), mimeType: 'application/json' });
                toast.success(`Exported ${filtered.length} rows as JSON`);
              }}>
                <FileText className="h-3.5 w-3.5 mr-2" /> JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                // OSCAL assessment-results style wrapper (minimal but real JSON file).
                const oscal = {
                  'assessment-results': {
                    uuid: crypto.randomUUID(),
                    metadata: { title: 'ArgusAI Audit Log Export', 'last-modified': new Date().toISOString(), version: '1.0', 'oscal-version': '1.1.2' },
                    results: [{
                      uuid: crypto.randomUUID(),
                      title: 'Audit events',
                      start: filtered[filtered.length - 1]?.timestamp ?? new Date().toISOString(),
                      end: filtered[0]?.timestamp ?? new Date().toISOString(),
                      observations: filtered.map(e => ({ uuid: e.id, description: e.action, collected: e.timestamp, props: [{ name: 'resource', value: e.resource }, { name: 'outcome', value: e.outcome }, { name: 'hash', value: e.fullHash }] })),
                    }],
                  },
                };
                triggerDownload({ filename: `audit-log-oscal-${new Date().toISOString().slice(0,10)}.json`, content: JSON.stringify(oscal, null, 2), mimeType: 'application/json' });
                toast.success(`Exported ${filtered.length} rows as OSCAL`);
              }}>
                <FileText className="h-3.5 w-3.5 mr-2" /> OSCAL
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Date Range</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setDateRangeOpen(true)}>
                <Calendar className="h-3.5 w-3.5 mr-2" /> Pick date range…
              </DropdownMenuItem>
              {customRange && (
                <DropdownMenuItem onClick={() => { setCustomRange(null); toast.success('Custom date range cleared'); }}>
                  <X className="h-3.5 w-3.5 mr-2" /> Clear custom range
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => toast.info('Recurring export scheduler opened')}>
                <Clock className="h-3.5 w-3.5 mr-2" /> Schedule Recurring…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Hash Chain Status Banner — honest empty state.
          Backend audit_logs currently writes sequence_number/record_hash/
          previous_hash as NULL (hash-chain compute is unimplemented).
          Until the chain is populated, do not claim "verified". */}
      <Card className="border-amber-500/30 bg-amber-500/[0.04]">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-amber-500/15 p-2.5">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                {/* div (not p): the Pending Badge renders a <div>, and a
                    <div> inside a <p> is invalid HTML — in the production React
                    build the browser-reparented DOM made the reconciler loop
                    (Maximum update depth / #185) and CRASH the whole Audit page
                    whenever this region re-rendered (e.g. opening the verify
                    dialog). Caught by the Playwright banner proof. */}
                <div className="text-sm font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                  {chainEnabled ? 'Hash Chain Active' : 'Hash Chain Not Yet Enabled'}
                  <Badge variant="outline" className="text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30">
                    {chainEnabled ? 'Tamper-evident' : 'Pending'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {chainEnabled
                    ? 'Audit rows are hash-chained: each record embeds the previous record’s hash. Use “Run Verification Now” to recompute and confirm the chain end-to-end.'
                    : 'Audit rows are being recorded, but tamper-evident hash-chain generation is not yet wired. Sequence numbers and record hashes will populate once the backend chain compute job ships.'}
                </p>
              </div>
            </div>

            <Separator orientation="vertical" className="h-12 hidden md:block" />

            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Records</p>
                <p className="text-lg font-semibold tabular-nums">{realAudit?.entries?.length != null ? entries.length.toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Last Sequence</p>
                <p className="text-lg font-semibold tabular-nums font-mono">
                  {entries[0]?.sequenceNumber ? `#${entries[0].sequenceNumber.toLocaleString()}` : '—'}
                </p>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10"
                onClick={() => setVerifyOpen(true)}
                title="Run full hash-chain verification now"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Run Verification Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats (24h) */}
      <div className="grid grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold tabular-nums">{stats24h.total.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Events (24h)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
              {stats24h.successful.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Successful
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <p className={cn(
              'text-2xl font-bold tabular-nums',
              stats24h.failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
            )}>
              {stats24h.failed.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <XCircle className="h-3 w-3" /> Failed
            </p>
          </CardContent>
        </Card>
        <Card className={stats24h.critical > 0 ? 'border-red-500/30 bg-red-500/[0.03]' : undefined}>
          <CardContent className="pt-4 pb-3 text-center">
            <p className={cn(
              'text-2xl font-bold tabular-nums',
              stats24h.critical > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
            )}>
              {stats24h.critical.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Critical
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search action, resource, user, IP, hash…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as typeof categoryFilter)}>
          <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {(Object.keys(CATEGORY_STYLES) as AuditCategory[]).map(c => (
              <SelectItem key={c} value={c}>{CATEGORY_STYLES[c].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={outcomeFilter} onValueChange={v => setOutcomeFilter(v as typeof outcomeFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Outcome" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Outcomes</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failure">Failure</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>

        <Select value={dateFilter} onValueChange={v => setDateFilter(v as typeof dateFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Last hour</SelectItem>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>

        <Select value={userFilter} onValueChange={v => setUserFilter(v)}>
          <SelectTrigger className="w-40 h-9 text-xs"><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {entities.users.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No users found</div>
            )}
            {entities.users.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={frameworkFilter} onValueChange={v => setFrameworkFilter(v as typeof frameworkFilter)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Framework" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Frameworks</SelectItem>
            {(Object.keys(FRAMEWORK_COLORS) as AuditFramework[]).map(f => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-xs text-muted-foreground ml-auto tabular-nums">
          {filtered.length.toLocaleString()} of {entries.length.toLocaleString()}
        </span>
      </div>

      {/* 2026-05-12 browser audit P1 — explicit error banner so a backend
          500 (Postgres bigint coercion, etc.) does not masquerade as
          "No audit events match your filters". */}
      {auditError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm"
        >
          <div className="font-medium text-destructive">
            Audit log failed to load
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {auditErrorObj instanceof Error
              ? auditErrorObj.message
              : 'The /trust-module/audit endpoint returned a non-success status. Check the API logs and retry.'}
          </div>
        </div>
      )}

      {/* Table */}
      {auditError ? null : sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto bg-card shadow-sm">
          <div className="min-w-[1500px]">
            <div className={cn(
              'grid gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider border-b',
              forensicsMode
                ? 'grid-cols-[110px_140px_110px_minmax(180px,1.5fr)_minmax(180px,1.5fr)_180px_110px_110px_120px_140px_60px]'
                : 'grid-cols-[110px_140px_110px_minmax(180px,1.5fr)_minmax(180px,1.5fr)_180px_110px_110px_140px_60px]',
            )}>
              <SortableHeader label="Seq #"    sortKey="sequenceNumber" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Timestamp" sortKey="timestamp"     currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Category" sortKey="category"       currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Action"   sortKey="action"         currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Resource" sortKey="resource"       currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="User"     sortKey="userName"       currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Outcome"  sortKey="outcome"        currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Class."   sortKey="classification" currentSort={sortConfig} onSort={handleSort} />
              {forensicsMode && <span className="text-muted-foreground">Session</span>}
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Hash className="h-3 w-3" /> Hash
              </span>
              <span className="text-muted-foreground text-right">Actions</span>
            </div>

            <div className="divide-y">
              {sorted.map(e => (
                <AuditRow
                  key={e.id}
                  entry={e}
                  forensicsMode={forensicsMode}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ScrollText className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">No audit events match your filters</p>
          <p className="text-xs text-muted-foreground mt-1">
            Try widening the date range, clearing the search, or disabling filters.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-4 gap-1.5"
            onClick={() => {
              setSearch('');
              setCategoryFilter('all');
              setOutcomeFilter('all');
              setUserFilter('all');
              setFrameworkFilter('all');
              setDateFilter('all');
            }}
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </Button>
        </div>
      )}

      {/* Detail Sheet */}
      <AuditDetailSheet
        entry={selected}
        related={selected ? relatedFor(selected) : []}
        onClose={() => setSelected(null)}
        onSelectRelated={setSelected}
      />

      {/* Hash-chain Verify Dialog */}
      <VerifyChainDialog
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
      />

      {/* Date Range Picker */}
      <DateRangePickerDialog
        open={dateRangeOpen}
        onOpenChange={setDateRangeOpen}
        initialRange={customRange}
        onApply={(range) => {
          setCustomRange(range);
          if (range) {
            toast.success('Custom date range applied', {
              description: `${range.start} → ${range.end}`,
            });
          }
        }}
      />
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

function AuditRow({
  entry, forensicsMode, onSelect,
}: {
  entry: AuditEntry;
  forensicsMode: boolean;
  onSelect: (e: AuditEntry) => void;
}) {
  const cat = CATEGORY_STYLES[entry.category];
  const out = OUTCOME_STYLES[entry.outcome];
  const cls = CLASSIFICATION_STYLES[entry.classification];
  const CatIcon = cat.icon;
  const OutIcon = out.icon;

  return (
    <div
      className={cn(
        'grid gap-3 px-4 py-2.5 items-center hover:bg-muted/30 transition-colors cursor-pointer group',
        forensicsMode
          ? 'grid-cols-[110px_140px_110px_minmax(180px,1.5fr)_minmax(180px,1.5fr)_180px_110px_110px_120px_140px_60px]'
          : 'grid-cols-[110px_140px_110px_minmax(180px,1.5fr)_minmax(180px,1.5fr)_180px_110px_110px_140px_60px]',
        entry.critical && 'border-l-4 border-l-red-500',
        entry.outcome === 'failure' && 'bg-red-500/[0.03]',
        entry.outcome === 'blocked' && 'bg-orange-500/[0.03]',
        entry.classification === 'ITAR' && cls.banner,
        entry.classification === 'PHI' && cls.banner,
        entry.classification === 'CUI' && cls.banner,
      )}
      onClick={() => onSelect(entry)}
    >
      {/* Sequence # — null until backend hash-chain compute job ships */}
      <span className="text-xs font-mono tabular-nums font-semibold text-muted-foreground">
        {entry.sequenceNumber ? `#${entry.sequenceNumber.toLocaleString()}` : '—'}
      </span>

      {/* Timestamp */}
      <span
        className="text-xs text-muted-foreground tabular-nums"
        title={formatDateTime(entry.timestamp)}
      >
        {timeAgo(entry.timestamp)}
      </span>

      {/* Category */}
      <Badge variant="outline" className={cn('text-[10px] w-fit gap-1', cat.color)}>
        <CatIcon className="h-3 w-3" /> {cat.label}
      </Badge>

      {/* Action */}
      <div className="flex items-center gap-1.5 min-w-0">
        <code className="text-xs font-mono truncate">{entry.action}</code>
        {entry.critical && (
          <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
        )}
      </div>

      {/* Resource */}
      <code className="text-xs font-mono text-muted-foreground truncate" title={entry.resource}>
        {entry.resource}
      </code>

      {/* User */}
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="text-xs truncate font-medium">{entry.userName}</p>
          <p className="text-[10px] text-muted-foreground font-mono truncate">{entry.ipAddress}</p>
        </div>
      </div>

      {/* Outcome */}
      <Badge variant="outline" className={cn('text-[10px] w-fit gap-1', out.color)}>
        <OutIcon className="h-3 w-3" /> {out.label}
      </Badge>

      {/* Classification */}
      {entry.classification === 'Unclassified' ? (
        <span className="text-[10px] text-muted-foreground">—</span>
      ) : (
        <Badge variant="outline" className={cn('text-[10px] w-fit', cls.color)}>
          {entry.classification}
        </Badge>
      )}

      {/* Forensics-only: Session */}
      {forensicsMode && (
        <code className="text-[10px] font-mono text-muted-foreground truncate" title={entry.sessionId}>
          {entry.sessionId}
        </code>
      )}

      {/* Hash — null until backend hash-chain compute job ships */}
      <code
        className="text-[10px] font-mono text-muted-foreground truncate"
        title={entry.fullHash || 'Hash chain not yet enabled'}
      >
        {entry.hash || '—'}
      </code>

      {/* Actions */}
      <div className="text-right" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Row actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => onSelect(entry)}>
              <Eye className="h-3.5 w-3.5 mr-2" /> View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => copyToClipboard(entry.fullHash, 'Hash')}>
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy Full Hash
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                // Real client-side export of this audit entry as JSON (the row is
                // already loaded). Replaced a toast that only claimed success.
                const blob = new Blob([JSON.stringify(entry, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-event-${entry.sequenceNumber}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success('Event exported as JSON');
              }}
            >
              <Download className="h-3.5 w-3.5 mr-2" /> Export Event
            </DropdownMenuItem>
            {/* "Verify From Here" and "Open in Forensics" removed — they were no-op
                toasts with no backend. Chain-integrity verification lives on the
                Trust chain view; re-add per-entry actions when their endpoints land. */}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ── Detail Sheet ────────────────────────────────────────────────────────────

function AuditDetailSheet({
  entry, related, onClose, onSelectRelated,
}: {
  entry: AuditEntry | null;
  related: AuditEntry[];
  onClose: () => void;
  onSelectRelated: (e: AuditEntry) => void;
}) {
  if (!entry) return null;

  const cat = CATEGORY_STYLES[entry.category];
  const out = OUTCOME_STYLES[entry.outcome];
  const cls = CLASSIFICATION_STYLES[entry.classification];
  const CatIcon = cat.icon;
  const OutIcon = out.icon;

  return (
    <Sheet open={!!entry} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent className="w-[720px] sm:max-w-[720px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className={cn(
          'px-6 py-4 border-b',
          entry.critical && 'bg-red-500/[0.04]',
          entry.classification !== 'Unclassified' && !entry.critical && cls.banner,
        )}>
          <div className="flex items-start gap-3">
            <div className={cn('rounded-lg p-2 flex-shrink-0', cat.color)}>
              <CatIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <Badge variant="outline" className={cn('text-[10px] gap-1 font-mono', cat.color)}>
                  <CatIcon className="h-3 w-3" /> {cat.label}
                </Badge>
                <Badge variant="outline" className={cn('text-[10px] gap-1', out.color)}>
                  <OutIcon className="h-3 w-3" /> {out.label}
                </Badge>
                {entry.classification !== 'Unclassified' && (
                  <Badge variant="outline" className={cn('text-[10px]', cls.color)}>
                    {entry.classification}
                  </Badge>
                )}
                {entry.critical && (
                  <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30 gap-0.5">
                    <AlertTriangle className="h-2.5 w-2.5" /> CRITICAL
                  </Badge>
                )}
              </div>
              <SheetTitle className="text-base leading-tight flex items-center gap-2">
                <span className="font-mono">#{(entry.sequenceNumber ?? 0).toLocaleString()}</span>
                <span className="text-muted-foreground text-sm font-normal">·</span>
                <code className="text-sm">{entry.action}</code>
              </SheetTitle>
              <SheetDescription className="text-xs mt-1 leading-relaxed">
                {formatDateTime(entry.timestamp)} · <span className="text-muted-foreground">{timeAgo(entry.timestamp)}</span>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-5">
            <Tabs defaultValue="details">
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                <TabsTrigger value="hashchain" className="flex-1">
                  <Hash className="h-3.5 w-3.5 mr-1" /> Hash Chain
                </TabsTrigger>
                <TabsTrigger value="metadata" className="flex-1">Metadata</TabsTrigger>
                <TabsTrigger value="related" className="flex-1">
                  Related <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1">{related.length}</Badge>
                </TabsTrigger>
              </TabsList>

              {/* ── Details ────────────────────────────────────────────── */}
              <TabsContent value="details" className="space-y-5 mt-5">
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" /> Event Details
                  </h3>
                  <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                    <Row label="Timestamp (ISO)" value={entry.timestamp} mono />
                    <Row label="Relative" value={timeAgo(entry.timestamp)} />
                    <Row label="Action" value={entry.action} mono />
                    <Row label="Resource" value={entry.resource} mono />
                    <Row label="Outcome" value={out.label} />
                    <Row label="Session" value={entry.sessionId} mono />
                    {entry.conversationId && <Row label="Conversation" value={entry.conversationId} mono />}
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" /> User
                  </h3>
                  <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                    <Row label="Name" value={entry.userName} />
                    <Row label="User ID" value={entry.userId} mono />
                    <Row label="Email" value={entry.userEmail} />
                    <Row label="IP Address" value={entry.ipAddress} mono />
                    <Row label="User Agent" value={entry.userAgent} mono />
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" /> Classification & Frameworks
                  </h3>
                  <div className="rounded-lg border p-3 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Classification:</span>
                      <Badge variant="outline" className={cn('text-[10px]', cls.color)}>
                        {entry.classification}
                      </Badge>
                    </div>
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground pt-0.5">Frameworks:</span>
                      {entry.frameworks.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                      {entry.frameworks.map(f => (
                        <Badge key={f} variant="outline" className={cn('text-[10px]', FRAMEWORK_COLORS[f])}>
                          {f}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* ── Hash Chain Proof ───────────────────────────────────── */}
              <TabsContent value="hashchain" className="space-y-5 mt-5">
                <div className="rounded-lg border bg-muted/30 p-3">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-semibold">
                      Hash-chain proof for this record
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Each record embeds the previous record's hash, so any change to this row (or an earlier
                    one) breaks the chain. Run “Verify This Record” below to recompute the chain on the
                    server and confirm integrity.
                  </p>
                </div>

                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Hash className="h-3.5 w-3.5" /> Cryptographic Proof
                  </h3>
                  <div className="rounded-lg border bg-muted/30 divide-y text-xs">
                    <Row label="Sequence #" value={`#${(entry.sequenceNumber ?? 0).toLocaleString()}`} mono />
                    <div className="px-3 py-2">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-muted-foreground">This Hash</span>
                        <Button
                          size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                          onClick={() => copyToClipboard(entry.fullHash, 'This hash')}
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      </div>
                      <code className="block text-[10px] font-mono bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 p-2 rounded break-all">
                        {entry.fullHash}
                      </code>
                    </div>
                    <div className="px-3 py-2">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-muted-foreground">Previous Hash</span>
                        <Button
                          size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]"
                          onClick={() => copyToClipboard(entry.previousHash, 'Previous hash')}
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      </div>
                      <code className="block text-[10px] font-mono bg-muted/60 p-2 rounded break-all">
                        {entry.previousHash}
                      </code>
                    </div>
                    <Row label="Algorithm" value="SHA-256" mono />
                    <Row label="Anchor" value="Daily Merkle root → external witness" />
                  </div>
                </section>

                <Button
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={async () => {
                    // The audit_logs sequence shown for this row does NOT map
                    // into the audit_chain table the verify endpoint walks
                    // (different sequence spaces), so a per-row range produced
                    // an inverted from>to and a vacuous 0-record "pass". Verify
                    // the FULL chain (fromSequence:1) — the only meaningful,
                    // honest operation — and report the real server result.
                    try {
                      const res = await apiClient.post<{
                        passed: boolean; recordsChecked: number;
                        firstFailureAt: number | null; firstFailureReason: string | null;
                      }>('/governance/audit/chain/verify', { fromSequence: 1 });
                      if (res.passed && res.recordsChecked > 0) {
                        toast.success('Hash chain verified', {
                          description: `Recomputed ${res.recordsChecked.toLocaleString()} record(s) on the server — all hashes match.`,
                        });
                      } else if (res.passed) {
                        toast.info('No hash-chained records to verify yet', {
                          description: 'The tamper-evident chain is empty for this tenant.',
                        });
                      } else {
                        toast.error('Chain verification FAILED', {
                          description: res.firstFailureReason ?? `Tampering detected at sequence #${res.firstFailureAt ?? '?'}.`,
                        });
                      }
                    } catch (err) {
                      toast.error('Could not verify chain', {
                        description: err instanceof Error ? err.message : 'The verification request failed.',
                      });
                    }
                  }}
                >
                  <ShieldCheck className="h-3.5 w-3.5" /> Verify This Record
                </Button>
              </TabsContent>

              {/* ── Metadata JSON ──────────────────────────────────────── */}
              <TabsContent value="metadata" className="mt-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> Detailed Metadata
                </h3>
                {entry.detailedMetadata ? (
                  <pre className="rounded-lg border bg-muted/30 p-3 text-[11px] font-mono overflow-x-auto leading-relaxed">
{JSON.stringify(entry.detailedMetadata, null, 2)}
                  </pre>
                ) : (
                  <div className="rounded-lg border bg-muted/30 p-6 text-center">
                    <p className="text-xs text-muted-foreground">No additional metadata recorded for this event.</p>
                  </div>
                )}
              </TabsContent>

              {/* ── Related Events ─────────────────────────────────────── */}
              <TabsContent value="related" className="mt-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Related Events (same session / conversation)
                </h3>
                {related.length === 0 ? (
                  <div className="rounded-lg border bg-muted/30 p-6 text-center">
                    <p className="text-xs text-muted-foreground">No related events in this session or conversation.</p>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card divide-y">
                    {related.map(r => {
                      const rcat = CATEGORY_STYLES[r.category];
                      const RIcon = rcat.icon;
                      return (
                        <button
                          key={r.id}
                          onClick={() => onSelectRelated(r)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/40 transition-colors text-left"
                        >
                          <Badge variant="outline" className={cn('text-[10px] gap-1 flex-shrink-0', rcat.color)}>
                            <RIcon className="h-3 w-3" /> {rcat.label}
                          </Badge>
                          <div className="min-w-0 flex-1">
                            <code className="text-xs font-mono block truncate">{r.action}</code>
                            <p className="text-[10px] text-muted-foreground truncate">
                              #{r.sequenceNumber.toLocaleString()} · {timeAgo(r.timestamp)} · {r.userName}
                            </p>
                          </div>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>

        {/* Footer actions */}
        <Separator />
        <div className="px-6 py-3 flex items-center gap-2 bg-muted/20">
          <Button
            size="sm" className="gap-1.5"
            onClick={() => {
              triggerDownload({
                filename: `audit-event-${entry.sequenceNumber ?? entry.id}.json`,
                content: JSON.stringify(entry, null, 2),
                mimeType: 'application/json',
              });
              toast.success(`Event #${(entry.sequenceNumber ?? 0).toLocaleString()} exported`);
            }}
            title="Export this event as JSON"
          >
            <Download className="h-3.5 w-3.5" /> Export Event
          </Button>
          <Button
            size="sm" variant="outline" className="gap-1.5"
            onClick={async () => {
              // Wire the formerly-dead footer button to the real full-chain
              // verify (the audit_logs row seq does not map into the
              // audit_chain space — see Verify This Record). Verify whole chain.
              try {
                const res = await apiClient.post<{
                  passed: boolean; recordsChecked: number;
                  firstFailureAt: number | null; firstFailureReason: string | null;
                }>('/governance/audit/chain/verify', { fromSequence: 1 });
                if (res.passed && res.recordsChecked > 0) {
                  toast.success('Hash chain verified', {
                    description: `Recomputed ${res.recordsChecked.toLocaleString()} record(s) on the server — all hashes match.`,
                  });
                } else if (res.passed) {
                  toast.info('No hash-chained records to verify yet');
                } else {
                  toast.error('Chain verification FAILED', {
                    description: res.firstFailureReason ?? `Tampering detected at sequence #${res.firstFailureAt ?? '?'}.`,
                  });
                }
              } catch (err) {
                toast.error('Could not verify chain', {
                  description: err instanceof Error ? err.message : 'The verification request failed.',
                });
              }
            }}
            title="Verify the hash chain on the server"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Verify Chain
          </Button>
          <Button
            size="sm" variant="outline" className="gap-1.5"
            onClick={() => toast.info('Forensics session reconstruction is not yet available', {
              description: 'Session + tool-call graph reconstruction is not wired to a backend yet.',
            })}
            title="Forensics reconstruction (not yet available)"
          >
            <ArrowUpRight className="h-3.5 w-3.5" /> Forensics
          </Button>
          <div className="ml-auto">
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" /> Close
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Hash-chain Verification Dialog ──────────────────────────────────────────

function VerifyChainDialog({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Platform audit 2026-06-10: this banner used to fake the verification with a
  // setInterval ramp that always reported "0 broken links" — on a compliance
  // surface, and contradicting the page's own honest "chain not yet enabled"
  // notice. It now calls the SAME real endpoint the activity drill-in uses:
  // POST /governance/audit/chain/verify (recomputes hashes server-side).
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<{
    passed: boolean;
    recordsChecked: number;
    firstFailureAt: number | null;
    firstFailureReason: string | null;
    from: number;
    to: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    if (running) return;
    setRunning(true);
    setDone(false);
    setResult(null);
    setError(null);
    try {
      const res = await apiClient.post<{
        passed: boolean;
        recordsChecked: number;
        firstFailureAt: number | null;
        firstFailureReason: string | null;
        verifiedRange: { from: number; to: number };
      }>('/governance/audit/chain/verify', { fromSequence: 1 });
      setResult({
        passed: res.passed,
        recordsChecked: res.recordsChecked,
        firstFailureAt: res.firstFailureAt,
        firstFailureReason: res.firstFailureReason,
        from: res.verifiedRange.from,
        to: res.verifiedRange.to,
      });
      setDone(true);
      if (res.passed) {
        toast.success('Hash chain verified', {
          description: `Checked ${res.recordsChecked.toLocaleString()} record(s) (#${res.verifiedRange.from}–#${res.verifiedRange.to}) on the server. All hashes match.`,
        });
      } else {
        toast.error('Chain verification FAILED', {
          description: res.firstFailureReason ?? `Tampering detected at sequence #${res.firstFailureAt ?? '?'}.`,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'The verification request failed.';
      setError(msg);
      toast.error('Could not verify chain', { description: msg });
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setRunning(false);
    setDone(false);
    setResult(null);
    setError(null);
  };
  const brokenLinks = result ? (result.passed ? 0 : 1) : 0;
  const processed = result?.recordsChecked ?? 0;
  const progress = done ? 100 : running ? 50 : 0;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Hash Chain Verification
          </DialogTitle>
          <DialogDescription>
            Recomputes every record's hash and verifies the chain end-to-end.
            This is the cryptographic proof that no audit row has been modified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Algorithm</span>
              <span className="font-mono">SHA-256 hash chain</span>
            </div>
          </div>

          {(running || done) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {running ? 'Recomputing hashes on the server…' : 'Complete'}
                </span>
                {done && (
                  <span className="font-mono tabular-nums">
                    {processed.toLocaleString()} records checked
                  </span>
                )}
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-[11px] text-muted-foreground text-right">
                {running ? 'verifying…' : `${Math.floor(progress)}%`}
              </p>
            </div>
          )}

          {done && (
            <div className={cn(
              'rounded-lg border p-3',
              brokenLinks === 0
                ? 'border-emerald-500/30 bg-emerald-500/5'
                : 'border-red-500/30 bg-red-500/5',
            )}>
              <div className="flex items-center gap-2">
                {brokenLinks === 0 ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      Chain intact — 0 broken links
                    </p>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                      {brokenLinks} broken link{brokenLinks !== 1 ? 's' : ''} detected
                    </p>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {result && result.passed
                  ? `All ${result.recordsChecked.toLocaleString()} hashes recompute correctly (#${result.from}–#${result.to}). No tampering detected.`
                  : result
                    ? (result.firstFailureReason ?? `Tampering detected at sequence #${result.firstFailureAt ?? '?'}.`)
                    : ''}
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => { reset(); onClose(); }}>
            Close
          </Button>
          {!running && !done && (
            <Button size="sm" className="gap-1.5" onClick={start}>
              <Play className="h-3.5 w-3.5" /> Start Verification
            </Button>
          )}
          {running && (
            <Button size="sm" variant="outline" className="gap-1.5" disabled>
              <Pause className="h-3.5 w-3.5" /> Verifying…
            </Button>
          )}
          {done && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={start}>
              <Play className="h-3.5 w-3.5" /> Re-run
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Small Helpers ───────────────────────────────────────────────────────────

function Row({
  label, value, mono, valueClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-muted-foreground flex-shrink-0">{label}</span>
      <span
        className={cn(
          'text-right truncate max-w-[65%]',
          mono && 'font-mono text-[11px]',
          valueClass,
        )}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

// ── Date Range Picker Dialog ────────────────────────────────────────────────

function DateRangePickerDialog({
  open, onOpenChange, initialRange, onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialRange: { start: string; end: string } | null;
  onApply: (range: { start: string; end: string } | null) => void;
}) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initialRange) {
      setStart(initialRange.start);
      setEnd(initialRange.end);
    } else {
      // Default: last 30 days
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 86_400_000);
      setStart(thirtyDaysAgo.toISOString().slice(0, 10));
      setEnd(today.toISOString().slice(0, 10));
    }
  }, [open, initialRange]);

  const presets: Array<{ label: string; days: number }> = [
    { label: 'Last 7 days',  days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 365 days', days: 365 },
  ];

  const applyPreset = (days: number) => {
    const today = new Date();
    const past = new Date(today.getTime() - days * 86_400_000);
    setStart(past.toISOString().slice(0, 10));
    setEnd(today.toISOString().slice(0, 10));
  };

  const validRange = start && end && new Date(start) <= new Date(end);
  const rangeDays = validRange
    ? Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000) + 1
    : 0;

  const handleApply = () => {
    if (!validRange) {
      toast.error('End date must be on or after start date');
      return;
    }
    onApply({ start, end });
    onOpenChange(false);
  };

  const handleClear = () => {
    onApply(null);
    onOpenChange(false);
    toast.success('Custom date range cleared');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Custom Date Range
          </DialogTitle>
          <DialogDescription>
            Filter audit events by exact date range. Applies across stats, filters, and exports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Presets</p>
            <div className="flex flex-wrap gap-1.5">
              {presets.map(p => (
                <Button
                  key={p.days}
                  size="sm"
                  variant="outline"
                  className="text-xs h-7"
                  onClick={() => applyPreset(p.days)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label htmlFor="start-date" className="text-xs font-semibold">Start date</label>
              <Input
                id="start-date"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                max={end || undefined}
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="end-date" className="text-xs font-semibold">End date</label>
              <Input
                id="end-date"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                min={start || undefined}
                max={new Date().toISOString().slice(0, 10)}
                className="h-9 text-xs"
              />
            </div>
          </div>

          {validRange && (
            <div className="text-xs px-3 py-2 rounded-md bg-muted/30 border text-muted-foreground">
              Range spans <strong className="text-foreground">{rangeDays} days</strong>
              {rangeDays > 90 && (
                <span className="text-amber-700 dark:text-amber-400 ml-2">
                  · Large range — may be slow to export
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {initialRange && (
            <Button variant="ghost" onClick={handleClear} className="mr-auto text-red-600 dark:text-red-400">
              Clear range
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleApply} disabled={!validRange}>
            <Calendar className="h-4 w-4 mr-1.5" /> Apply range
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
